import {
  dxtToLocalServerTime,
  ErrorModal,
  getMomentTz,
  HelmetDexteel,
  ShiftDayNavigatorControl,
  useShiftNavigator,
} from "@dexteel/mesf-core";
import { FilterList } from "@mui/icons-material";
import { Box, Button, Grid2, Paper, Tooltip, Typography } from "@mui/material";
import type { GridApi } from "ag-grid-community";
import type { Moment } from "moment";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { useDebouncedStrandLossFilter } from "./hooks/useDebouncedStrandLossFilter";
import { EndFilter, StartFilter } from "../../delays/shared/StartEndFilter";
import {
  type NavigationMode,
  NavigationModeToggle,
  StrandLossesManagerFilters,
} from "./components/filters";
import { StrandLossesManagerTable } from "./components/table/StrandLossesManagerTable";
import { FILTER_BAR_HEIGHT } from "./theme/dimensions";
import type {
  StrandLossByShift,
  StrandLossHeat,
} from "./models/StrandLossByShift";
import { useStrandAssetsQuery } from "./hooks/queries/useStrandAssetsQuery";
import { useStrandLossesQuery } from "./hooks/queries/useStrandLossesByShiftQuery";

const moment = getMomentTz();

const PAGE_TITLE = "Strand Losses Manager";
const MIN_MINUTES_DEBOUNCE_MS = 400;
const EXCEL_FILE_PREFIX = "Strand-Losses";
const EXCEL_FILE_DATE_FORMAT = "YYYY-MM-DD";
const EXPORT_COLUMN_KEYS = [
  "Heats",
  "Strand",
  "Start",
  "End",
  "Duration",
  "Category",
  "StrandLossCodeName",
  "Comments",
  "JustifiedByUser",
  "JustifiedDateTime",
];

const EXCEL_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
const DATE_TIME_COLUMN_KEYS = ["Start", "End", "JustifiedDateTime"] as const;

type DateTimeColumnKey = (typeof DATE_TIME_COLUMN_KEYS)[number];

const isDateTimeColumn = (columnId: string): columnId is DateTimeColumnKey =>
  DATE_TIME_COLUMN_KEYS.includes(columnId as DateTimeColumnKey);

const formatDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((unit) => String(unit).padStart(2, "0"))
    .join(":");
};

const readNumberParam = (rawValue: string | null): number | null => {
  if (rawValue === null || rawValue === "") return null;
  const parsedValue = Number.parseInt(rawValue, 10);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

export const StrandLossesManagerPage = () => {
  const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
  const { shiftId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [showMicrolosses, setShowMicrolosses] = useState(false);
  const [navMode, setNavMode] = useState<NavigationMode>("shift");
  const [showFilters, setShowFilters] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const [loadedRoute, setLoadedRoute] = useState<string | null>(null);
  const [filterStartDate, setFilterStartDate] = useState<Moment>(
    moment().subtract(1, "days").startOf("day"),
  );
  const [filterEndDate, setFilterEndDate] = useState<Moment>(
    moment().endOf("day"),
  );
  const gridApiRef = useRef<GridApi<StrandLossByShift> | null>(null);
  const routeShiftId = shiftId ?? "";
  const isShiftReady = loadedRoute === routeShiftId;
  const strandLossIdParam = readNumberParam(searchParams.get("strandLossId"));
  const crew = searchParams.get("crew") || null;
  const strandLossCodeId = readNumberParam(searchParams.get("codeId"));
  const strandLossCategoryId = readNumberParam(searchParams.get("categoryId"));
  const strand = readNumberParam(searchParams.get("strand"));
  const minMinutes = readNumberParam(searchParams.get("minMinutes"));

  const updateFilterParams = (updates: Record<string, string | null>) => {
    const nextParams = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "") nextParams.delete(key);
      else nextParams.set(key, value);
    }
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    // Changes made by the shift control already have their data in context.
    if (loadedRoute === routeShiftId) return;
    let current = true;
    void getShiftDataFromAPI(null, shiftId ? Number(shiftId) : 0)
      .then((shift) => {
        if (!current) return;
        if (!shift) setError("Failed to load shift");
        setLoadedRoute(routeShiftId);
      })
      .catch((loadError: unknown) => {
        if (current)
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Failed to load shift",
          );
      });
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load only for route changes, not context updates
  }, [shiftId]);

  useEffect(() => {
    const currentShiftId = shiftInfo?.CurrentShiftId;
    if (!isShiftReady || !currentShiftId || String(currentShiftId) === shiftId)
      return;

    // Mark this route as loaded before navigating so it does not fetch again.
    setLoadedRoute(String(currentShiftId));
    navigate(
      {
        pathname: `/strand-losses-manager/${currentShiftId}`,
        search: searchParams.toString(),
      },
      { replace: !shiftId },
    );
  }, [
    isShiftReady,
    shiftInfo?.CurrentShiftId,
    shiftId,
    searchParams,
    navigate,
  ]);

  const { data: assetScope, isLoading: isLoadingAssets } = useStrandAssetsQuery(
    (queryError) => setError(queryError.message),
  );

  const isShiftMode = navMode === "shift";
  const debouncedMinMinutes = useDebouncedStrandLossFilter(
    minMinutes,
    MIN_MINUTES_DEBOUNCE_MS,
  );
  const { data: strandLossesResult, isFetching: isFetchingStrandLosses } =
    useStrandLossesQuery(
      {
        shiftId: isShiftMode ? (shiftInfo?.CurrentShiftId ?? null) : null,
        startDate: isShiftMode ? null : filterStartDate.format("YYYY-MM-DD"),
        endDate: isShiftMode ? null : filterEndDate.format("YYYY-MM-DD"),
        includeMicroDelays: showMicrolosses,
        crew,
        strandLossCodeId,
        strandLossCategoryId,
        strand,
        minMinutes: debouncedMinMinutes,
      },
      isShiftMode ? isShiftReady && !!shiftInfo?.CurrentShiftId : true,
      (queryError) => setError(queryError.message),
    );

  const strandLosses = strandLossesResult?.rows ?? [];
  const summary = strandLossesResult?.summary ?? {
    count: 0,
    durationSeconds: 0,
  };

  const exportFileSuffix = isShiftMode
    ? `${moment.utc(shiftInfo?.CurrentProductionDate).format(EXCEL_FILE_DATE_FORMAT)}_Shift-${shiftInfo?.CurrentShift ?? ""}-${shiftInfo?.CurrentCrew ?? ""}`
    : `${filterStartDate.format(EXCEL_FILE_DATE_FORMAT)}_${filterEndDate.format(EXCEL_FILE_DATE_FORMAT)}`;

  const handleExportToExcel = () => {
    gridApiRef.current?.exportDataAsExcel({
      fileName: `${EXCEL_FILE_PREFIX}-${exportFileSuffix}.xlsx`,
      columnKeys: EXPORT_COLUMN_KEYS,
      processCellCallback: (params) => {
        const columnId = params.column.getColId();
        if (columnId === "Heats") {
          const heats = (params.node?.data?.Heats ?? []) as StrandLossHeat[];
          return heats.map((heat) => heat.HeatName).join(", ");
        }
        if (isDateTimeColumn(columnId)) {
          return (
            dxtToLocalServerTime(
              params.node?.data?.[columnId],
              EXCEL_DATE_TIME_FORMAT,
            ) || ""
          );
        }
        if (columnId === "Duration") {
          return formatDuration(Number(params.value) || 0);
        }
        if (columnId === "Category") {
          return params.node?.data?.StrandLossCategoryName ?? "";
        }
        return params.value;
      },
    });
  };

  const headerDate = isShiftMode
    ? shiftInfo?.CurrentStart.toLocaleDateString()
    : `${filterStartDate.format("MM/DD/YYYY")} - ${filterEndDate.format("MM/DD/YYYY")}`;

  return (
    <>
      <HelmetDexteel title={`${headerDate} - ${PAGE_TITLE}`} />
      <Grid2 container size={{ md: 12, xs: 12 }} spacing={0.5} px={2} pt={1}>
        <Grid2 size={{ md: 12, xs: 12 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {PAGE_TITLE}
          </Typography>
        </Grid2>
        <Grid2 size={{ md: 12, xs: 12 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
            }}
          >
            <NavigationModeToggle value={navMode} onChange={setNavMode} />
            {isShiftMode ? (
              <Box
                sx={{
                  height: FILTER_BAR_HEIGHT,
                  "& .MuiPaper-root": {
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  },
                }}
              >
                <ShiftDayNavigatorControl preventInit />
              </Box>
            ) : (
              <Paper
                elevation={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  height: FILTER_BAR_HEIGHT,
                }}
              >
                <Box sx={{ minWidth: 160 }}>
                  <StartFilter
                    value={filterStartDate}
                    onChange={(date) => date && setFilterStartDate(date)}
                    maxDate={filterEndDate}
                    size="small"
                  />
                </Box>
                <Box sx={{ minWidth: 160 }}>
                  <EndFilter
                    value={filterEndDate}
                    onChange={(date) => date && setFilterEndDate(date)}
                    minDate={filterStartDate}
                    size="small"
                  />
                </Box>
              </Paper>
            )}
            <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  minWidth: 40,
                  px: 1,
                  height: FILTER_BAR_HEIGHT,
                  backgroundColor: showFilters ? "grey.500" : "primary.main",
                  "&:hover": {
                    backgroundColor: showFilters ? "grey.600" : "primary.dark",
                  },
                }}
              >
                <FilterList />
              </Button>
            </Tooltip>
            <Box sx={{ ml: "auto", minWidth: 180 }}>
              <ExcelButton
                handleExportToExcel={handleExportToExcel}
                disabled={isFetchingStrandLosses || strandLosses.length === 0}
              />
            </Box>
          </Box>
        </Grid2>
        {showFilters && (
          <Grid2 size={{ md: 12, xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              <StrandLossesManagerFilters
                parentAssetId={assetScope?.parentAssetId ?? null}
                strands={assetScope?.strands ?? []}
                strandLossCodeId={strandLossCodeId}
                strandLossCategoryId={strandLossCategoryId}
                strand={strand}
                crew={crew}
                minMinutes={minMinutes}
                quickFilter={quickFilter}
                showMicrolosses={showMicrolosses}
                onStrandLossCodeChange={(value) =>
                  updateFilterParams({ codeId: value ? String(value) : null })
                }
                onStrandLossCategoryChange={(value) =>
                  updateFilterParams({
                    categoryId: value ? String(value) : null,
                  })
                }
                onStrandChange={(value) =>
                  updateFilterParams({ strand: value ? String(value) : null })
                }
                onCrewChange={(value) => updateFilterParams({ crew: value })}
                onMinMinutesChange={(value) =>
                  updateFilterParams({
                    minMinutes: value === null ? null : String(value),
                  })
                }
                onQuickFilterChange={setQuickFilter}
                onShowMicrolossesChange={setShowMicrolosses}
              />
            </Box>
          </Grid2>
        )}
        <Grid2 size={{ md: 12, xs: 12 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              minHeight: 24,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Count:{" "}
              <span style={{ fontWeight: 400 }}>
                {summary.count.toLocaleString()}
              </span>
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Duration:{" "}
              <span style={{ fontWeight: 400 }}>
                {formatDuration(summary.durationSeconds)}
              </span>
            </Typography>
          </Box>
        </Grid2>
        <Grid2 size={{ md: 12, xs: 12 }}>
          <StrandLossesManagerTable
            isLoading={
              (isShiftMode && !isShiftReady) ||
              isLoadingAssets ||
              isFetchingStrandLosses
            }
            strandLosses={strandLosses}
            parentAssetId={assetScope?.parentAssetId ?? null}
            strandAssets={assetScope?.strands ?? []}
            strandLossId={strandLossIdParam ?? undefined}
            quickFilter={quickFilter}
            onGridApiReady={(api) => {
              gridApiRef.current = api;
            }}
          />
        </Grid2>
      </Grid2>
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Strand Losses Manager error"
      />
    </>
  );
};
